// Tracking, abstracted: one interface, two backends, so the camera interface
// degrades instead of dying when MediaPipe (or the CDN, or the GPU) is not
// available. Nothing here knows about regions, sounds or the command API --
// it turns a playing <video> into a stream of normalised points.
//
// TIMESTAMPS. The study measures movement time to a region entry, so the
// point must carry the time of the FRAME it came from, not the time the
// callback ran. In order of preference:
//
//   1. `requestVideoFrameCallback` metadata `captureTime` -- when the camera
//      captured the frame, already on the performance.now() timeline. This is
//      the only value that excludes the capture->compositor pipeline.
//   2. metadata `presentationTime` -- when the UA submitted the frame for
//      composition. Later than capture by the pipeline delay (tens of ms).
//   3. `performance.now()` at frame grab (the rAF fallback path). Later again
//      by up to one frame interval of polling jitter.
//
// Limits worth stating in the deck: Firefox has no rVFC at all and Safari
// does not populate `captureTime`, so the same participant on two browsers is
// offset by an unknown constant. The bias is roughly constant WITHIN a
// device, so within-device comparisons survive it and cross-device ones do
// not -- the same caveat the spec already makes about `asynchrony_ms`.

// Pinned. The WASM blobs are fetched from a CDN rather than bundled: Vite
// would have to be taught to emit the .wasm and its loader as assets and to
// keep them side by side, which is more trouble than it is worth for a file
// that never changes between builds. The version here MUST match the version
// of @mediapipe/tasks-vision in package.json.
const MEDIAPIPE_VERSION = '1.0.1'
const WASM_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}/wasm`
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task'

const INDEX_FINGERTIP = 8      // MediaPipe hand landmark index
const NO_HAND_MS = 4000        // 'auto' gives MediaPipe this long to see a hand

// Blob thresholds, all in 0..255 luma on the downsampled frame.
const BLOB_W = 64
const BLOB_H = 48
const BLOB_MIN_LUMA = 90       // below this the frame is too dark to have a target
const BLOB_MIN_CONTRAST = 40   // the blob must beat the frame mean by this much
const BLOB_TOP_FRACTION = 0.8  // ...and sit in the top 20% of the range
const BLOB_MAX_AREA = 0.25     // more than this and we are tracking the room, not a torch

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v)

// --- backends --------------------------------------------------------------
// Each backend is { name, detect(video, t) -> point|null, close() }.

async function createMediapipeBackend() {
  // Dynamic so that a CDN or WASM failure is a caught rejection rather than a
  // dead module graph, and so the ~3 MB bundle is not in the entry chunk.
  const vision = await import('@mediapipe/tasks-vision')
  const fileset = await vision.FilesetResolver.forVisionTasks(WASM_BASE)

  const options = {
    baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
    runningMode: 'VIDEO',
    numHands: 1,
  }
  let landmarker
  try {
    landmarker = await vision.HandLandmarker.createFromOptions(fileset, options)
  } catch {
    // Lab machines and older phones routinely refuse the GPU delegate.
    options.baseOptions.delegate = 'CPU'
    landmarker = await vision.HandLandmarker.createFromOptions(fileset, options)
  }

  // detectForVideo rejects a timestamp that does not advance, and captureTime
  // can repeat if the camera stalls; nudge rather than throw away the frame.
  let lastTs = -1

  return {
    name: 'mediapipe',
    detect(video, t) {
      if (!video.videoWidth) return null
      const ts = t > lastTs ? t : lastTs + 1
      lastTs = ts
      const res = landmarker.detectForVideo(video, ts)
      const lm = res?.landmarks?.[0]?.[INDEX_FINGERTIP]
      if (!lm) return null
      return {
        x: clamp01(lm.x),
        y: clamp01(lm.y),
        confidence: res.handedness?.[0]?.[0]?.score ?? 1,
      }
    },
    close() {
      try { landmarker.close() } catch { /* already gone */ }
    },
  }
}

// The spec's fallback: the brightest blob on a heavily downsampled frame.
// A phone torch, a white glove or a desk lamp all work; ambient light does
// not, which is what the thresholds are for.
function createBlobBackend() {
  const canvas = document.createElement('canvas')
  canvas.width = BLOB_W
  canvas.height = BLOB_H
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  const lum = new Float32Array(BLOB_W * BLOB_H)

  return {
    name: 'blob',
    detect(video) {
      if (!video.videoWidth || !ctx) return null
      ctx.drawImage(video, 0, 0, BLOB_W, BLOB_H)
      const { data } = ctx.getImageData(0, 0, BLOB_W, BLOB_H)

      let max = 0
      let sum = 0
      for (let i = 0, p = 0; p < lum.length; i += 4, p++) {
        const l = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114
        lum[p] = l
        sum += l
        if (l > max) max = l
      }
      const mean = sum / lum.length
      if (max < BLOB_MIN_LUMA || max - mean < BLOB_MIN_CONTRAST) return null

      // Centroid of the top slice only, so an evenly lit room reports nothing
      // instead of a point drifting about the middle of the frame.
      const threshold = Math.max(mean + BLOB_MIN_CONTRAST, max * BLOB_TOP_FRACTION)
      let wx = 0
      let wy = 0
      let w = 0
      let n = 0
      for (let p = 0; p < lum.length; p++) {
        if (lum[p] < threshold) continue
        const weight = lum[p] - threshold
        wx += (p % BLOB_W) * weight
        wy += ((p / BLOB_W) | 0) * weight
        w += weight
        n++
      }
      if (w <= 0 || n > lum.length * BLOB_MAX_AREA) return null

      return {
        x: (wx / w + 0.5) / BLOB_W,
        y: (wy / w + 0.5) / BLOB_H,
        confidence: Math.min(1, (max - mean) / 128),
      }
    },
    close() {},
  }
}

// --- the tracker -----------------------------------------------------------

// createTracker({ backend, video })
//   start()            -> Promise; begins the frame loop, resolves once a
//                         backend is live (or rejects if none could load)
//   stop()             -> cancels every pending frame callback
//   onPoint(cb)        -> cb({ x, y, t, confidence }); x,y normalised 0..1 in
//                         VIDEO coordinates, never mirrored -- mirroring is a
//                         view concern and the interface handles it
//   onLost(cb)         -> cb(t) for a frame with no point, so a caller can
//                         reset a dwell rather than hold it across a dropout
//   onStatus(cb)       -> cb({ backend, state, message }) whenever the live
//                         backend or its state changes, so the UI can say
//                         which one the person is actually using
// Each subscribe returns an unsubscribe.
export function createTracker({ backend = 'auto', video } = {}) {
  const pointListeners = new Set()
  const lostListeners = new Set()
  const statusListeners = new Set()

  let active = null          // the live backend object
  let running = false
  let rvfcHandle = 0
  let rafHandle = 0
  let usingRvfc = false
  let sawPoint = false
  let startedAt = 0
  let demoting = false       // an auto-fallback swap is in flight
  let status = { backend: null, state: 'idle', message: '' }

  function setStatus(next) {
    status = { ...status, ...next }
    for (const fn of statusListeners) fn(status)
  }

  function emitPoint(p) {
    for (const fn of pointListeners) fn(p)
  }

  function emitLost(t) {
    for (const fn of lostListeners) fn(t)
  }

  // Frame time, best source first; see the TIMESTAMPS note at the top.
  function frameTime(now, metadata) {
    if (metadata) {
      if (typeof metadata.captureTime === 'number') return metadata.captureTime
      if (typeof metadata.presentationTime === 'number') return metadata.presentationTime
    }
    return typeof now === 'number' ? now : performance.now()
  }

  function onFrame(now, metadata) {
    if (!running) return
    schedule()
    if (!active) return

    const t = frameTime(now, metadata)
    let p = null
    try {
      p = active.detect(video, t)
    } catch {
      // A single bad frame (a resize mid-detect, a GPU hiccup) must not end
      // the session; if it is systemic the no-hand timer will demote us.
      p = null
    }

    if (p) {
      sawPoint = true
      emitPoint({ x: p.x, y: p.y, t, confidence: p.confidence ?? 1 })
    } else {
      emitLost(t)
      maybeDemote(t)
    }
  }

  // 'auto' falls back to blob when MediaPipe loads but never sees a hand.
  // The demotion is one-way and only ever happens before the FIRST point: a
  // hand that has been seen once and then leaves the frame is a person
  // resting, not a broken backend, and swapping backends mid-round would
  // invalidate the trial.
  function maybeDemote(t) {
    if (backend !== 'auto' || demoting || sawPoint) return
    if (active?.name !== 'mediapipe') return
    if (t - startedAt < NO_HAND_MS) return
    demoting = true
    swapTo(createBlobBackend(), 'no hand seen -- using brightness tracking')
    demoting = false
  }

  function swapTo(next, message) {
    active?.close()
    active = next
    setStatus({ backend: next.name, state: 'tracking', message })
  }

  function schedule() {
    if (!running) return
    if (usingRvfc) rvfcHandle = video.requestVideoFrameCallback(onFrame)
    else rafHandle = requestAnimationFrame(onFrame)
  }

  return {
    get backend() { return active?.name ?? null },
    get status() { return status },

    async start() {
      if (running) return status
      if (!video) throw new Error('camera tracker needs a <video> element')

      // rVFC fires once per decoded frame and carries the metadata we want;
      // rAF fires on the display clock and carries none of it.
      usingRvfc = typeof video.requestVideoFrameCallback === 'function'

      setStatus({ state: 'loading', message: 'loading hand tracking…' })
      if (backend === 'blob') {
        active = createBlobBackend()
        setStatus({ backend: 'blob', state: 'tracking', message: 'brightness tracking' })
      } else {
        try {
          active = await createMediapipeBackend()
          setStatus({ backend: 'mediapipe', state: 'tracking', message: 'hand tracking' })
        } catch (err) {
          if (backend === 'mediapipe') {
            setStatus({ backend: null, state: 'error', message: String(err?.message ?? err) })
            throw err
          }
          active = createBlobBackend()
          setStatus({
            backend: 'blob',
            state: 'tracking',
            message: 'hand tracking unavailable -- using brightness tracking',
          })
        }
      }

      running = true
      sawPoint = false
      startedAt = performance.now()
      schedule()
      return status
    },

    stop() {
      running = false
      if (rvfcHandle && typeof video?.cancelVideoFrameCallback === 'function') {
        video.cancelVideoFrameCallback(rvfcHandle)
      }
      if (rafHandle) cancelAnimationFrame(rafHandle)
      rvfcHandle = 0
      rafHandle = 0
      active?.close()
      active = null
      setStatus({ backend: null, state: 'idle', message: '' })
    },

    onPoint(fn) { pointListeners.add(fn); return () => pointListeners.delete(fn) },
    onLost(fn) { lostListeners.add(fn); return () => lostListeners.delete(fn) },
    onStatus(fn) { statusListeners.add(fn); return () => statusListeners.delete(fn) },
  }
}
